import { Progress } from '@tempo-io/sui-progress';
import { useTranslation } from 'react-i18next';
import { ActualCostStatusIndicator } from '@/features/financials/components/common/ActualCostStatusIndicator';
import { getDeltaColorClass } from '@/features/loop-financials/utils/budgetVsActual';
import { Divider, Row, Section } from './financialPanelLayout';
import type { ItemFinancialFigures } from './types';

interface CostSectionProps {
  figures?: ItemFinancialFigures;
  effectiveBudget: number | null;
  money: (value: number | null | undefined) => string;
  rate: (value: number) => string;
  isLowestLevel?: boolean;
  scheduleElapsedPercent?: number | null;
  expectedSpendToDate?: number | null;
  straightLineDelta?: number | null;
  showHeaderValue?: boolean;
}

const getBudgetUsedPercent = (
  totalActualCost: number | null,
  effectiveBudget: number | null,
): number | null => {
  if (totalActualCost == null || effectiveBudget == null) return null;
  if (effectiveBudget > 0) return Math.round((totalActualCost / effectiveBudget) * 100);
  return totalActualCost > 0 ? 100 : 0;
};

export const CostSection = ({
  figures,
  effectiveBudget,
  money,
  rate,
  isLowestLevel = false,
  scheduleElapsedPercent = null,
  expectedSpendToDate = null,
  straightLineDelta = null,
  showHeaderValue = false,
}: CostSectionProps) => {
  const { t } = useTranslation();
  const selfActualCost = figures?.selfActualCost ?? null;
  const selfActualCostStatus = figures?.selfActualCostStatus ?? null;
  const rolledActualCost = figures?.totalActualCost ?? null;
  const rolledActualCostStatus = figures?.totalActualCostStatus ?? null;
  const hasUnavailableActualCost =
    selfActualCostStatus === 'UNAVAILABLE' || rolledActualCostStatus === 'UNAVAILABLE';
  if (
    !figures ||
    (selfActualCost == null && rolledActualCost == null && !hasUnavailableActualCost)
  ) {
    return null;
  }

  const totalActualCost = isLowestLevel ? (selfActualCost ?? rolledActualCost) : rolledActualCost;
  const totalActualCostStatus = isLowestLevel
    ? selfActualCost != null
      ? selfActualCostStatus
      : rolledActualCostStatus
    : rolledActualCostStatus;
  const descendantActualCost =
    totalActualCost != null ? totalActualCost - (selfActualCost ?? 0) : null;
  const selfActualHours = figures.selfActualHours ?? null;
  const selfActualRate =
    selfActualCost != null && selfActualHours ? selfActualCost / selfActualHours : null;
  const budgetUsedPercent = getBudgetUsedPercent(totalActualCost, effectiveBudget);
  const budgetRemaining =
    totalActualCost != null && effectiveBudget != null ? effectiveBudget - totalActualCost : null;

  return (
    <Section
      title={t(($) => $.financials.panel.costSectionTitle)}
      subtitle={t(($) => $.financials.panel.costSectionSubtitle)}
      headerValue={showHeaderValue ? money(totalActualCost) : undefined}
    >
      {(selfActualCost != null || selfActualCostStatus === 'UNAVAILABLE') && (
        <Row
          label={t(($) => $.financials.panel.selfActualCost)}
          sublabel={
            selfActualRate != null && selfActualHours != null
              ? t(($) => $.financials.panel.selfActualCostSublabel, {
                  hours: selfActualHours,
                  rate: rate(selfActualRate),
                })
              : undefined
          }
          right={
            <div className="flex items-center justify-end gap-1.5">
              {money(selfActualCost)}
              <ActualCostStatusIndicator status={selfActualCostStatus} />
            </div>
          }
        />
      )}
      {descendantActualCost != null && !isLowestLevel && (
        <Row
          label={t(($) => $.financials.panel.descendantActualCost)}
          sublabel={t(($) => $.financials.panel.descendantActualCostSublabel)}
          right={money(descendantActualCost)}
        />
      )}
      {(totalActualCost != null || totalActualCostStatus === 'UNAVAILABLE') && (
        <Row
          label={t(($) => $.financials.panel.totalActualCost)}
          sublabel={
            isLowestLevel ? undefined : t(($) => $.financials.panel.totalActualCostSublabel)
          }
          right={
            <div className="flex items-center justify-end gap-1.5">
              {money(totalActualCost)}
              <ActualCostStatusIndicator status={totalActualCostStatus} />
            </div>
          }
          variant={showHeaderValue ? 'totalHighlighted' : 'total'}
        />
      )}
      {budgetUsedPercent != null && (
        <div className="flex flex-col gap-1.5 pt-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-neutral-primary">
              {t(($) => $.financials.panel.budgetRemaining)}
            </span>
            <span className="text-xs font-medium text-text-neutral-secondary">
              {t(($) => $.financials.panel.budgetRemainingUsed, {
                amount: money(budgetRemaining),
                percent: budgetUsedPercent,
              })}
            </span>
          </div>
          <Progress value={Math.min(budgetUsedPercent, 100)} max={100} />
        </div>
      )}
      {scheduleElapsedPercent != null && (
        <>
          <Divider />
          <Row
            label={t(($) => $.financials.budgetPace.scheduleElapsedLabel)}
            right={`${scheduleElapsedPercent}%`}
          />
          {expectedSpendToDate != null && straightLineDelta != null && (
            <>
              <Row
                label={t(($) => $.financials.budgetPace.straightLineLabel)}
                right={money(expectedSpendToDate)}
              />
              <Row
                label={
                  straightLineDelta >= 0
                    ? t(($) => $.financials.budgetPace.underStraightLine)
                    : t(($) => $.financials.budgetPace.overStraightLine)
                }
                right={
                  <span className={getDeltaColorClass(straightLineDelta)}>
                    {money(Math.abs(straightLineDelta))}
                  </span>
                }
              />
            </>
          )}
        </>
      )}
    </Section>
  );
};
